import { ComponentDataSourceService } from '../../component-data-source.service';
import { Observable, map, catchError, timeout, of, filter, take, switchMap } from 'rxjs';
import { DataSourceFunction } from '../../component-data-source.service';
import { LoadDataParams } from '../../../../interfaces/DataSource/DataSourceParams.interface';
import { environment } from '../../../../environment/environment';
import { EventDTO } from '../../../../dto/event/event.dto';
import { AuthService } from '../../../../services/auth/auth.service';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../ngrx/template/template.reducers';
import * as EventsActions from '../../../../ngrx/events/events.actions';
import * as EventsSelectors from '../../../../ngrx/events/events.selectors';
import {
  EventCategoryColorsMap,
  normalizeEventCategoryColors,
  buildEventCategoryInheritanceContext,
  resolveEventCategoryBackgroundColor,
} from './event-category-color.util';

type EventType = 'education' | 'course' | 'exercise' | 'activity';

// Danish labels for event types in tables/calendar
const EVENT_TYPE_LABELS: Record<string, string> = {
  exercise: 'Øvelser',
  education: 'Uddannelser',
  course: 'Kurser',
  activity: 'Aktiviteter',
};
function getEventTypeLabel(type: string): string {
  return EVENT_TYPE_LABELS[type] ?? type;
}

// Define the event color mapping
function getEventColor(type: EventType): string {
  switch (type) {
    case 'education':
      return '#4caf50'; // Green
    case 'exercise':
      return '#2196f3'; // Blue
    case 'course':
      return '#ff9800'; // Orange
    case 'activity':
      return '#66bb6a'; // Light green
    default:
      return '#9e9e9e'; // Grey
  }
}

// Define the Syncfusion Calendar event interface
interface SyncfusionEvent {
  Id: number;
  Subject: string;
  StartTime: Date;
  EndTime: Date;
  ResourceId: string;
  Description: string;
  CategoryColor: string;
  IsReadonly: boolean;
  IsBlock: boolean;
  Status: string;
  MaxParticipants?: number;
  Type: string;
  IsActive: boolean;
  IsShown: boolean;
}

export const event_calendar = (service: ComponentDataSourceService): DataSourceFunction => ({
  name: 'event_calendar',
  description: 'Get events for calendar view',
  execute: (params?: LoadDataParams): Observable<SyncfusionEvent[]> => {
    // Get Store and AuthService from injector
    const store = service.injector.get(Store<AppState>);
    const authService = service.injector.get(AuthService);
    
    // Extract queryParams from params
    const queryParams: any = params?.queryParams || {};
    
    // Handle date range filters for calendar view (legacy support)
    if (params?.filters) {
      const startFilter = params.filters.find(f => f.column === 'startDate');
      const endFilter = params.filters.find(f => f.column === 'endDate');
      if (startFilter) queryParams.startDate = queryParams.startDate || startFilter.value;
      if (endFilter) queryParams.endDate = queryParams.endDate || endFilter.value;
      
      // Preserve any additional filters
      params.filters.forEach(filter => {
        if (filter.column !== 'startDate' && filter.column !== 'endDate') {
          queryParams[filter.column] = queryParams[filter.column] || filter.value;
        }
      });
    }
    
    // Legacy support for old filter names
    if (params?.filters) {
      const startFilter = params.filters.find(f => f.column === 'start');
      const endFilter = params.filters.find(f => f.column === 'end');
      if (startFilter) queryParams.startDate = queryParams.startDate || startFilter.value;
      if (endFilter) queryParams.endDate = queryParams.endDate || endFilter.value;
    }

    // Dispatch action to load calendar events through NGRX
    store.dispatch(EventsActions.loadCalendarEvents({ queryParams }));
    
    // Return observable that waits for loading to complete and then gets the data
    const loading$ = store.select(EventsSelectors.selectCalendarLoading);
    const data$ = store.select(EventsSelectors.selectCalendarData);
    
    // Simplified strategy: Wait for loading to become true, then wait for it to become false
    // This ensures we get the data from our request
    return loading$.pipe(
      // Wait for loading to become true (our action was processed)
      filter(loading => loading === true),
      take(1),
      // Then wait for loading to become false (request completed)
      switchMap(() => loading$.pipe(
        filter(loading => loading === false),
        take(1),
        // Once loading is false, get the data
        switchMap(() => data$.pipe(
          filter(data => data !== null && data !== undefined),
          take(1),
          switchMap((events: EventDTO[]) => {
            const user = authService.getCurrentUser() as Record<string, unknown> | null;
            const orgId =
              user?.['organizations_id'] ??
              (user?.['user_info'] as Record<string, unknown> | undefined)?.['organizations_id'];
            const n = typeof orgId === 'number' ? orgId : parseInt(String(orgId ?? ''), 10);
            if (!Number.isFinite(n) || n <= 0) {
              return of({ events, categoryColors: {} as EventCategoryColorsMap });
            }
            const settingsObs = service.executeDataSource('org_settings', {
              organizationId: n,
            });
            if (!settingsObs) {
              return of({ events, categoryColors: {} as EventCategoryColorsMap });
            }
            return settingsObs.pipe(
              take(1),
              map((res: { data?: Record<string, unknown> }) => ({
                events,
                categoryColors: normalizeEventCategoryColors(
                  res?.data?.['event_category_colors']
                ),
              })),
              catchError(() =>
                of({ events, categoryColors: {} as EventCategoryColorsMap })
              )
            );
          })
        ))
      )),
      // Map the events to Syncfusion format
      map(({ events, categoryColors }: { events: EventDTO[]; categoryColors: EventCategoryColorsMap }) => {
        if (!events || !Array.isArray(events)) {
          return [];
        }
        
        // Filter out 'exercise' events if user is not authenticated
        let filteredEvents = events;
        if (!authService.isAuthenticated) {
          filteredEvents = events.filter(event => event.event_type !== 'exercise');
        }
        
        // Filter out events that are not shown for non-authenticated users
        if (!authService.isAuthenticated) {
          filteredEvents = filteredEvents.filter(event => event.is_shown !== false);
        }
        
        const categoryContext = buildEventCategoryInheritanceContext(filteredEvents);
        const mappedEvents = filteredEvents.map(event => {
          
          // Ensure is_active is properly handled
          const isActive = event.is_active !== undefined ? event.is_active : true;
          const categoryColor =
            resolveEventCategoryBackgroundColor(
              event,
              categoryColors,
              categoryContext
            ) ||
            getEventColor(event.event_type as EventType);
          
          return {
            Id: event.event_id,
            Subject: event.description.short_description['da']?.translation_text || '',
            StartTime: new Date(event.start_date),
            EndTime: new Date(event.end_date),
            ResourceId: event.predefined_location?.guid || '1',
            Description: event.description.long_description['da']?.translation_text || '',
            CategoryColor: categoryColor,
            IsReadonly: !isActive,
            IsBlock: !isActive,
            Status: isActive ? 'active' : 'inactive',
            MaxParticipants: event.manual_location_room_capacity,
            Type: getEventTypeLabel(event.event_type),
            // Add the is_active property for the calendar component
            IsActive: isActive,
            // Add the is_shown property for the calendar component
            IsShown: event.is_shown !== false
          };
        });
        return mappedEvents;
      }),
      // Add timeout to prevent hanging
      timeout(30000), // 30 second timeout
      catchError(error => {
        console.error('[event_calendar] Error processing events from store:', error);
        // Always return an empty array instead of completing without emitting
        return of([]);
      })
    );
  }
}); 